import { useEffect, useRef } from "react";
import { Eye, EyeOff, Trash2, X } from "lucide-react";
import { useSvgDiagram2D } from "./SvgDiagram2DContext";
import type { NodeStylePatch } from "./SvgDiagram2DContext";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { NODE_SHAPES } from "../../utils/svgDiagram2DModel";
import type { DiagramNode, NodeShape } from "../../utils/svgDiagram2DModel";

const SHAPE_LABELS: Record<NodeShape, string> = {
    circle: "Circle",
    square: "Square",
    diamond: "Diamond",
    triangle: "Triangle",
};

/**
 * Whether 2+ nodes are selected together. When true the multi-node panel takes
 * over and the single-node inspector suppresses itself so only one panel shows.
 */
export function isMultiNodeSelection(sel: {
    selectedNodeIds: string[];
}): boolean {
    return sel.selectedNodeIds.length >= 2;
}

function num(v: string | number, fallback: number): number {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
}

/** A common value across the selection, or the `mixed` sentinel when they differ. */
const MIXED = Symbol("mixed");
type CommonOf<T> = T | typeof MIXED;

/**
 * Reduce a property across the selected nodes to either the single shared value
 * (every node agrees) or `MIXED` (they differ).
 */
function common<T>(
    nodes: DiagramNode[],
    pick: (n: DiagramNode) => T,
): CommonOf<T> | undefined {
    let acc: T | undefined;
    let seen = false;
    for (const n of nodes) {
        const v = pick(n);
        if (!seen) {
            acc = v;
            seen = true;
        } else if (!Object.is(acc as T, v)) {
            return MIXED;
        }
    }
    return seen ? (acc as T) : undefined;
}

/**
 * Floating inspector shown when 2+ nodes are selected together. It edits the
 * appearance fields they share — color, size, shape, visibility, and heat
 * reactivity — applying each change to every selected node at once. A field
 * whose selected nodes disagree shows "Mixed"; editing it sets them all to the
 * new value. Spawn clones in the selection are skipped (their appearance
 * follows their template).
 *
 * Renders alongside the single-node panel, which suppresses itself while a
 * multi-node selection is active (see `isMultiNodeSelection`).
 */
export function SvgDiagram2DMultiNodePanel() {
    const {
        doc,
        selectedNodeIds,
        nodePanelOpen,
        updateSelectedNodesStyle,
        removeSelectedNodes,
        clearSelection,
    } = useSvgDiagram2D();

    const selected = doc.nodes.filter((n) => selectedNodeIds.includes(n.id));
    // Only the non-clone nodes are editable; clones follow their template.
    const editable = selected.filter((n) => !n.spawnedFrom);

    if (!nodePanelOpen || selected.length < 2) return null;

    const apply = (patch: NodeStylePatch) => updateSelectedNodesStyle(patch);

    const color = common(editable, (n) => n.color);
    const colorMixed = color === MIXED;
    const radius = common(editable, (n) => n.radius);
    const radiusMixed = radius === MIXED;
    const shape = common(editable, (n) => n.shape);
    const shapeMixed = shape === MIXED;
    const visible = common(editable, (n) => n.visible);
    const visibleMixed = visible === MIXED;
    const heat = common(editable, (n) => n.heatReactive ?? false);
    const heatMixed = heat === MIXED;

    const cloneCount = selected.length - editable.length;
    const noneEditable = editable.length === 0;

    return (
        <div className="diagram-node-panel">
            <div className="field-row" style={{ alignItems: "center", gap: 6 }}>
                <strong style={{ flex: 1, minWidth: 0 }}>
                    {selected.length} nodes selected
                </strong>
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => clearSelection()}
                    title="Clear selection"
                    style={{ padding: "2px 6px" }}
                >
                    <X size={14} aria-hidden />
                </button>
            </div>

            <p
                className="path-tool-meta"
                style={{ margin: "0 0 8px", opacity: 0.7 }}
            >
                Edits apply to all selected nodes. Fields that differ show
                "Mixed".
                {cloneCount > 0 &&
                    ` ${cloneCount} spawn clone${cloneCount > 1 ? "s" : ""} skipped.`}
            </p>

            {/* --- Properties --- */}
            <div className="diagram-node-panel-section">
                <p className="diagram-node-panel-section-title">Properties</p>

                {noneEditable ? (
                    <p className="path-tool-meta" style={{ margin: 0 }}>
                        All selected nodes are spawn clones — edit their template
                        to change their appearance.
                    </p>
                ) : (
                    <>
                        <div className="field-row field-row--two">
                            <label className="field-label">
                                Shape{shapeMixed ? " (Mixed)" : ""}
                                <select
                                    className="anchor-select"
                                    value={
                                        shapeMixed
                                            ? "__mixed__"
                                            : (shape as NodeShape)
                                    }
                                    onChange={(e) => {
                                        if (e.target.value === "__mixed__")
                                            return;
                                        apply({
                                            shape: e.target.value as NodeShape,
                                        });
                                    }}
                                >
                                    {shapeMixed && (
                                        <option value="__mixed__">
                                            — Mixed —
                                        </option>
                                    )}
                                    {NODE_SHAPES.map((s) => (
                                        <option key={s} value={s}>
                                            {SHAPE_LABELS[s]}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="field-label">
                                Size{radiusMixed ? " (Mixed)" : ""}
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={
                                        radiusMixed ? "" : (radius as number)
                                    }
                                    placeholder={radiusMixed ? "Mixed" : undefined}
                                    onChange={(v) =>
                                        apply({
                                            radius: Math.max(
                                                1,
                                                num(
                                                    v,
                                                    radiusMixed
                                                        ? 10
                                                        : (radius as number),
                                                ),
                                            ),
                                        })
                                    }
                                />
                            </label>
                        </div>

                        <label className="field-label">
                            Color{colorMixed ? " (Mixed)" : ""}
                            <input
                                type="color"
                                value={
                                    colorMixed ? "#000000" : (color as string)
                                }
                                onChange={(e) =>
                                    apply({ color: e.target.value })
                                }
                            />
                        </label>

                        <VisibilityToggle
                            mixed={visibleMixed}
                            visible={visibleMixed ? true : (visible as boolean)}
                            onChange={(v) => apply({ visible: v })}
                        />

                        <HeatToggle
                            mixed={heatMixed}
                            checked={heatMixed ? false : (heat as boolean)}
                            onChange={(v) => apply({ heatReactive: v })}
                        />
                    </>
                )}
            </div>

            {/* --- Danger --- */}
            <div className="diagram-node-panel-section">
                <button
                    type="button"
                    className="generic-button"
                    onClick={() => removeSelectedNodes()}
                    title="Delete all selected nodes"
                >
                    <Trash2 size={14} aria-hidden /> Delete {selected.length}{" "}
                    nodes
                </button>
            </div>
        </div>
    );
}

/**
 * A visibility checkbox that renders the indeterminate (dash) state when the
 * selection's `visible` flags disagree; toggling it from there shows everything.
 */
function VisibilityToggle({
    mixed,
    visible,
    onChange,
}: {
    mixed: boolean;
    visible: boolean;
    onChange: (v: boolean) => void;
}) {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = mixed;
    }, [mixed]);

    return (
        <label
            className="field-label"
            style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                marginTop: 6,
            }}
        >
            <input
                ref={ref}
                type="checkbox"
                checked={visible}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                }}
            >
                {visible ? (
                    <Eye size={14} aria-hidden />
                ) : (
                    <EyeOff size={14} aria-hidden />
                )}
                Visible{mixed ? " (Mixed)" : ""}
            </span>
        </label>
    );
}

/**
 * Heat-reactivity checkbox with an indeterminate state when the selection's
 * flags disagree; toggling it from there turns heat reactivity on for all.
 */
function HeatToggle({
    mixed,
    checked,
    onChange,
}: {
    mixed: boolean;
    checked: boolean;
    onChange: (v: boolean) => void;
}) {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = mixed;
    }, [mixed]);

    return (
        <label
            className="field-label"
            style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                marginTop: 6,
            }}
        >
            <input
                ref={ref}
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span>React to heat zones{mixed ? " (Mixed)" : ""}</span>
        </label>
    );
}
